import { org_sector_type_enum } from '@predium/enums';
import { PreTFunction } from '@predium/i18n/client';
import { PATHS } from '@/routes';

export type BreadcrumbNode = {
  label: string;
  href?: string;
  children?: Record<string, BreadcrumbNode>;
};

// Extracts the last URL segment, ignoring query strings.
const seg = (path: string) => path.split('/').filter(Boolean).at(-1) ?? '';

export function getBreadcrumbConfig(t: PreTFunction, orgSector: org_sector_type_enum): Record<string, BreadcrumbNode> {
  return {
    [seg(PATHS.statusQuoAnalysis.root())]: {
      label: t('General_StatusQuoAnalysis'),
      children: {
        [seg(PATHS.statusQuoAnalysis.buildings())]: {
          label: t('General_Building', { count: 2 }),
        },
        [seg(PATHS.statusQuoAnalysis.portfolio())]: {
          label: t('General_Portfolio', { count: 2 }),
        },
        // Sector-dependent segments — PATHS picks one URL segment at runtime so both are kept as literals.
        'economic-unit': {
          label: t('General_EconomicUnit', { orgSector: org_sector_type_enum.STANDARD, count: 2 }),
        },
        transaction: {
          label: t('General_EconomicUnit', { orgSector: org_sector_type_enum.BANKING, count: 2 }),
        },
      },
    },
    [seg(PATHS.actionPlanning.root())]: {
      label: t('General_ActionPlanning'),
      children: {
        [seg(PATHS.actionPlanning.scenarios())]: {
          label: t('PageName_Scenarios'),
          href: PATHS.actionPlanning.scenarios(),
          children: {
            [seg(PATHS.actionPlanning.scenario({ scenarioId: Number() }))]: {
              label: t('PageName_Scenario'),
              children: {
                'action-plans': { label: t('General_ActionPlan', { count: 1 }) },
              },
            },
          },
        },
      },
    },
    [seg(PATHS.reporting.root())]: {
      label: t('General_Reporting'),
      children: {
        [seg(PATHS.reporting.reports())]: {
          label: t('Reporting_Report', { count: 2 }),
          href: PATHS.reporting.reports(),
          children: {
            [seg(PATHS.reporting.report({ id: Number() }))]: {
              label: t('Reporting_Report', { count: 1 }),
              href: PATHS.reporting.report({ id: Number() }),
            },
          },
        },
      },
    },
    [seg(PATHS.dataCollection.root())]: {
      label: t('General_DataCollection'),
      children: {
        [seg(PATHS.dataCollection.buildings())]: {
          label: t('General_Building', { count: 2 }),
          href: PATHS.dataCollection.buildings(),
          children: {
            [seg(PATHS.dataCollection.building({ id: Number() }))]: {
              label: t('General_Building', { count: 1 }),
              href: PATHS.dataCollection.building({ id: Number() }),
            },
          },
        },
        // DC always uses 'economic-units' segment regardless of sector — label is sector-aware.
        [seg(PATHS.dataCollection.economicUnits())]: {
          label: t('General_EconomicUnit', { count: 2, orgSector }),
          href: PATHS.dataCollection.economicUnits(),
          children: {
            [seg(PATHS.dataCollection.economicUnit({ id: Number() }))]: {
              label: t('General_EconomicUnit', { count: 1, orgSector }),
              href: PATHS.dataCollection.economicUnit({ id: Number() }),
            },
          },
        },
        [seg(PATHS.dataCollection.portfolios())]: {
          label: t('General_Portfolio', { count: 2 }),
          href: PATHS.dataCollection.portfolios(),
        },
        [seg(PATHS.dataCollection.energySuppliers())]: {
          label: t('DataCollection_EnergySupplier', { count: 2 }),
          href: PATHS.dataCollection.energySuppliers(),
        },
        [seg(PATHS.dataCollection.energyNetworks())]: {
          label: t('DataCollection_EnergyNetwork', { count: 2 }),
          href: PATHS.dataCollection.energyNetworks(),
        },
        [seg(PATHS.dataCollection.consumptionHub())]: {
          label: t('General_ConsumptionHub'),
          href: PATHS.dataCollection.consumptionHub(),
        },
        [seg(PATHS.dataCollection.consumptionInvoices())]: {
          label: t('General_ConsumptionInvoices'),
          href: PATHS.dataCollection.consumptionInvoices(),
        },
      },
    },
    [seg(PATHS.documents.root())]: {
      label: t('General_Document', { count: 2 }),
      children: {
        [seg(PATHS.documents.dataInbox())]: {
          label: t('PageName_DataInbox'),
          href: PATHS.documents.dataInbox(),
        },
      },
    },
    [seg(PATHS.drive.root())]: {
      label: t('General_Drive'),
    },
    [seg(PATHS.org.root())]: {
      label: t('General_Admin'),
      children: {
        [seg(PATHS.org.users())]: {
          label: t('PageName_ManageUsers'),
          href: PATHS.org.users(),
          children: {
            [seg(PATHS.org.user({ id: Number() }))]: {
              label: t('General_User'),
              href: PATHS.org.user({ id: Number() }),
            },
          },
        },
        [seg(PATHS.org.settings())]: {
          label: t('Settings_ManageOrganization'),
          href: PATHS.org.settings(),
        },
      },
    },
  };
}
